import React, { useEffect, useState } from 'react';
import { MonitorIcon, MoonIcon, SunIcon } from './icons';

export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'fp.theme';

export const readThemePreference = (): ThemePreference => {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
};

export const applyThemePreference = (preference: ThemePreference) => {
  const root = document.documentElement;
  if (preference === 'system') {
    delete root.dataset.theme;
  } else {
    root.dataset.theme = preference;
  }
};

const writeThemePreference = (preference: ThemePreference) => {
  try {
    if (preference === 'system') {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    // Storage unavailable (private mode, blocked site data): theme still applies for this session.
  }
};

const OPTIONS: Array<{ value: ThemePreference; label: string; Icon: React.FC<{ size?: number }> }> = [
  { value: 'light', label: 'Light theme', Icon: SunIcon },
  { value: 'dark', label: 'Dark theme', Icon: MoonIcon },
  { value: 'system', label: 'Match system theme', Icon: MonitorIcon },
];

const ThemeToggle: React.FC = () => {
  const [preference, setPreference] = useState<ThemePreference>(readThemePreference);

  useEffect(() => {
    applyThemePreference(preference);
  }, [preference]);

  return (
    <div className="theme-toggle" role="group" aria-label="Color theme">
      {OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          title={label}
          aria-label={label}
          aria-pressed={preference === value}
          onClick={() => {
            setPreference(value);
            writeThemePreference(value);
          }}
        >
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
};

export default ThemeToggle;
